import React, { useState, useEffect, useRef } from 'react';
import { Form, Alert, Row, Col } from 'react-bootstrap';
import { FiUpload, FiX, FiFile, FiDownload } from 'react-icons/fi';
import FileViewer from 'react-file-viewer';
import { isFileTypeAllowed, getFileInputAccept, getFileTypeCategory } from '../../../utils/documentTypes';

const FileUploadSection = ({ 
  files = [], 
  onFilesChange, 
  checkingDuplicates = false,
  hideFileUpload = false,
  // Existing file info for edit mode
  existingFileName = null,
  existingFileSize = null,
  existingFileUrl = null,
  onDownloadExistingFile = null
}) => {
  const [fileTypeErrors, setFileTypeErrors] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Update preview when files change
  useEffect(() => {
    if (files.length > 0) {
      const firstFile = files[0];
      setPreviewFile(firstFile);
      const fileType = getFileTypeCategory(firstFile.name);
      
      // Create blob URL for all file types that need preview
      const url = URL.createObjectURL(firstFile);
      setPreviewUrl(url);
      
      // Cleanup: revoke blob URL when component unmounts or file changes
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
      setPreviewFile(null);
    }
  }, [files]);

  // Handle file viewer errors - prevent page crash
  const onError = (e) => {
    console.error('Error loading file preview:', e);
    console.log('File:', previewFile?.name);
  };

  // Process files with validation (only one file at a time)
  const processFiles = (selectedFiles) => {
    const errors = [];
    const validFiles = [];

    // Only take the first file
    const firstFile = selectedFiles[0];
    
    if (firstFile) {
      if (isFileTypeAllowed(firstFile.name)) {
        validFiles.push(firstFile);
      } else {
        errors.push(firstFile.name);
      }
    }

    if (errors.length > 0) {
      setFileTypeErrors(errors);
    } else {
      setFileTypeErrors([]);
    }

    if (onFilesChange && validFiles.length > 0) {
      onFilesChange(validFiles);
    }
  };

  // Handle file change with validation
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    processFiles(selectedFiles);
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files || []);
    processFiles(droppedFiles);
  };

  // Remove file
  const handleRemoveFile = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    if (onFilesChange) {
      onFilesChange(newFiles);
    }
  };

  // If hideFileUpload is true, still show existing file info but hide upload area
  if (hideFileUpload && !existingFileName) {
    return null;
  }

  return (
    <>
      <style>{`
        @media (max-width: 767.98px) {
          .file-upload-dropzone {
            padding: 15px 12px !important;
            min-height: 80px !important;
          }
          
          .upload-mobile-container {
            gap: 8px;
          }
          
          .upload-icon-mobile {
            flex-shrink: 0;
          }
          
          .upload-text-mobile {
            white-space: nowrap;
          }
        }
      `}</style>
      <Row className="mb-4">
      {/* Upload Area - Left Side - Hide if hideFileUpload is true */}
      {!hideFileUpload && (
        <Col xs={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="text-label">
              <span className="d-none d-md-inline">Upload Document</span>
              <span className="d-inline d-md-none">Upload File</span>
              <span className="text-danger">*</span>
            </Form.Label>
            <div
              ref={dropZoneRef}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className="file-upload-dropzone"
              style={{
                border: `2px dashed ${isDragging ? '#667eea' : '#ced4da'}`,
                borderRadius: '8px',
                padding: '40px 20px',
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: isDragging ? '#f8f9ff' : '#f8f9fa',
                transition: 'all 0.3s ease',
                minHeight: '300px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <div className="d-none d-md-block">
                <FiUpload size={48} className="upload-icon" style={{ color: '#667eea', marginBottom: '16px' }} />
                <p className="upload-text-primary" style={{ margin: '8px 0', fontSize: '16px', fontWeight: 500, color: '#495057' }}>
                  {isDragging ? 'Drop file here' : 'Drag and drop file here'}
                </p>
                <p className="upload-text-secondary" style={{ margin: '4px 0', fontSize: '14px', color: '#6c757d' }}>
                  or <span style={{ color: '#667eea', textDecoration: 'underline' }}>browse</span> to upload
                </p>
              </div>
              <div className="d-flex d-md-none align-items-center justify-content-center upload-mobile-container">
                <FiUpload size={24} className="upload-icon-mobile" style={{ color: '#667eea', marginRight: '8px' }} />
                <span className="upload-text-mobile" style={{ fontSize: '14px', fontWeight: 500, color: '#495057' }}>
                  Tap to upload file
                </span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept={getFileInputAccept()}
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <small className="form-text text-muted mt-2" style={{ display: 'block' }}>
                Allowed types: PDF, Word, Excel, Image (PNG/JPEG/GIF), Notepad
              </small>
            </div>
            
            {fileTypeErrors.length > 0 && (
              <Alert variant="danger" className="mt-2">
                <strong>Invalid file types:</strong>
                <ul className="mb-0 mt-2" style={{ paddingLeft: '20px' }}>
                  {fileTypeErrors.map((fileName, index) => (
                    <li key={index}>{fileName}</li>
                  ))}
                </ul>
                <small>Allowed types: PDF, Word, Excel, Image (PNG/JPEG/GIF), Notepad</small>
              </Alert>
            )}

            {/* Show existing file info in edit mode */}
            {existingFileName && files.length === 0 && (
              <div className="mb-3">
                <small className="text-muted">
                  Editing: <strong>{existingFileName}</strong>
                  {existingFileSize && (
                    <span> ({(existingFileSize / 1024 / 1024).toFixed(2)} MB)</span>
                  )}
                </small>
              </div>
            )}

            {files.length > 0 && (
              <div className="mt-3">
                <strong>Selected file:</strong>
                <div className="mt-2">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                      fontSize: '0.9rem'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                      <FiFile className="me-2" style={{ flexShrink: 0 }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {files[0].name}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '8px' }}>
                      <small className="text-muted" style={{ flexShrink: 0 }}>
                        {(files[0].size / 1024 / 1024).toFixed(2)} MB
                      </small>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile(0);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#dc3545',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <FiX size={18} />
                      </button>
                    </div>
                  </div>
                </div>
                {checkingDuplicates && (
                  <div className="mt-2 p-2" style={{ backgroundColor: '#e7f3ff', border: '1px solid #2196F3', borderRadius: '4px' }}>
                    <small className="text-info">
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Checking for duplicates and renaming if needed...
                    </small>
                  </div>
                )}
              </div>
            )}
          </Form.Group>
        </Col>
      )}

      {/* Existing File Display - Show when hideFileUpload is true and file exists */}
      {hideFileUpload && existingFileName && (
        <Col xs={12}>
          <div className="mb-3">
            <small className="text-muted">
              Editing: <strong>{existingFileName}</strong>
              {existingFileSize && (
                <span> ({(existingFileSize / 1024 / 1024).toFixed(2)} MB)</span>
              )}
            </small>
          </div>
        </Col>
      )}

      {/* Preview Area - Right Side - Hide in edit mode when existing file is shown and no new file uploaded */}
      {!(existingFileName && files.length === 0) && (
        <Col xs={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="text-label">Preview</Form.Label>
            <div
              style={{
                border: '1px solid #ced4da',
                borderRadius: '8px',
                padding: '20px',
                minHeight: '300px',
                backgroundColor: '#f8f9fa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column'
              }}
            >
            {previewFile ? (
              <>
                {(() => {
                  const fileType = previewFile.name.split('.').pop()?.toLowerCase() || '';
                  const fileTypeCategory = getFileTypeCategory(previewFile.name);

                  // Excel files cannot be previewed - show message
                  if (fileType === 'xls' || fileType === 'xlsx') {
                    return (
                      <div style={{ textAlign: 'center' }}>
                        <FiFile size={64} style={{ color: '#6c757d', marginBottom: '16px' }} />
                        <p className="text-muted">Excel files cannot be previewed in the browser</p>
                        <p className="text-muted" style={{ fontSize: '0.9rem', marginTop: '8px' }}>{previewFile.name}</p>
                        <p className="text-muted" style={{ fontSize: '0.85rem', marginTop: '4px' }}>
                          {(previewFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    );
                  }

                  // Use iframe for PDFs as browsers handle them natively
                  if (fileTypeCategory === 'pdf' && previewUrl) {
                    return (
                      <iframe
                        src={`${previewUrl}#toolbar=0`}
                        style={{
                          width: '100%',
                          height: '400px',
                          border: 'none',
                          borderRadius: '4px'
                        }}
                        title="PDF Preview"
                      />
                    );
                  }

                  // Use img tag for images
                  if (fileTypeCategory === 'image' && previewUrl) {
                    return (
                      <img
                        src={previewUrl}
                        alt={previewFile.name}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '400px',
                          objectFit: 'contain',
                          borderRadius: '4px'
                        }}
                      />
                    );
                  }

                  // List of file types that FileViewer supports well
                  const supportedPreviewTypes = ['doc', 'docx', 'ppt', 'pptx', 'txt', 'csv', 'jpg', 'jpeg', 'png', 'gif'];
                  
                  // Check if file type is supported for FileViewer
                  if (supportedPreviewTypes.includes(fileType) && previewUrl) {
                    return (
                      <div style={{ width: '100%', height: '400px', overflow: 'auto' }}>
                        <FileViewer
                          fileType={fileType}
                          filePath={previewUrl}
                          onError={onError}
                          errorComponent={<div className="text-center p-4"><p className="text-muted">Unable to preview this file type</p></div>}
                          unsupportedComponent={<div className="text-center p-4"><p className="text-muted">File type not supported for preview</p></div>}
                        />
                      </div>
                    );
                  }

                  // Fallback for unsupported types
                  return (
                    <div style={{ textAlign: 'center' }}>
                      <FiFile size={64} style={{ color: '#6c757d', marginBottom: '16px' }} />
                      <p className="text-muted">Preview not available for this file type</p>
                      <p className="text-muted" style={{ fontSize: '0.9rem', marginTop: '8px' }}>{previewFile.name}</p>
                      <p className="text-muted" style={{ fontSize: '0.85rem', marginTop: '4px' }}>
                        {(previewFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  );
                })()}
              </>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <FiFile size={64} style={{ color: '#ced4da', marginBottom: '16px' }} />
                <p className="text-muted">No file selected</p>
                <p className="text-muted" style={{ fontSize: '0.9rem' }}>Upload a file to see preview</p>
              </div>
            )}
          </div>
        </Form.Group>
      </Col>
      )}
    </Row>
    </>
  );
};

export default FileUploadSection;
